import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import type { InvoiceDefaults, InvoiceFieldIssue, InvoiceFieldKey, InvoiceFieldValues } from "@/lib/invoice-send";

interface InvoiceReferenceFieldsProps {
  values: InvoiceFieldValues;
  defaults: InvoiceDefaults;
  issues: InvoiceFieldIssue[];
  onChange: <K extends InvoiceFieldKey>(field: K, value: InvoiceFieldValues[K]) => void;
  sendNow: boolean;
  deliveryType: string;
}

const SOURCE_LABEL: Record<InvoiceDefaults[InvoiceFieldKey]["source"], string | null> = {
  project: "fra prosjektet",
  customer: "fra kundekortet",
  none: null,
};

const ISSUE_INPUT: Record<InvoiceFieldIssue["severity"], string> = {
  error: "border-destructive focus-visible:ring-destructive",
  warning: "border-amber-500 focus-visible:ring-amber-500",
};

const ISSUE_TEXT: Record<InvoiceFieldIssue["severity"], string> = {
  error: "text-destructive",
  warning: "text-amber-700",
};

/**
 * Deres ref., Deres ref. kode, betalingsfrist og e-post i opprett-faktura-dialogen.
 * Verdiene er forhåndsutfylt fra prosjektkortet med kundekortet som fallback
 * (`resolveInvoiceDefaults`), og et felt som mangler markeres rødt (stopper
 * sending) eller gult (påminnelse) med forklaring på hvor det fylles ut.
 */
export function InvoiceReferenceFields({
  values,
  defaults,
  issues,
  onChange,
  sendNow,
  deliveryType,
}: InvoiceReferenceFieldsProps) {
  const issueFor = (field: InvoiceFieldKey) => issues.find((i) => i.field === field);
  const emailDisabled = !sendNow || (deliveryType !== "Auto" && deliveryType !== "PdfByEmail");

  const field = (
    key: InvoiceFieldKey,
    label: string,
    input: React.ReactNode,
    extra?: React.ReactNode,
  ) => {
    const issue = issueFor(key);
    const source = SOURCE_LABEL[defaults[key].source];
    return (
      <div>
        <label htmlFor={`invoice-${key}`} className="text-xs text-muted-foreground mb-1 block">
          {label}
          {extra}
          {source && values[key] !== "" && (
            <span className="text-[11px] text-muted-foreground/70 ml-1">({source})</span>
          )}
        </label>
        {input}
        {issue && (
          <p data-testid={`invoice-issue-${key}`} className={cn("text-[11px] mt-1", ISSUE_TEXT[issue.severity])}>
            {issue.message}
          </p>
        )}
      </div>
    );
  };

  const inputClass = (key: InvoiceFieldKey) => {
    const issue = issueFor(key);
    return issue ? ISSUE_INPUT[issue.severity] : undefined;
  };

  return (
    <div className="grid grid-cols-2 gap-3">
      {field(
        "customerReference",
        "Deres ref.",
        <Input
          id="invoice-customerReference"
          className={inputClass("customerReference")}
          value={values.customerReference}
          onChange={(e) => onChange("customerReference", e.target.value)}
          placeholder="Navn hos kunden"
        />,
      )}
      {field(
        "purchaseOrderReference",
        "Deres ref. kode",
        <Input
          id="invoice-purchaseOrderReference"
          className={inputClass("purchaseOrderReference")}
          value={values.purchaseOrderReference}
          onChange={(e) => onChange("purchaseOrderReference", e.target.value)}
          placeholder="Kundens PO/referansenummer"
        />,
      )}
      {field(
        "paymentDueDays",
        "Betalingsfrist (dager)",
        <Input
          id="invoice-paymentDueDays"
          type="number"
          min={0}
          className={inputClass("paymentDueDays")}
          value={values.paymentDueDays}
          onChange={(e) =>
            onChange("paymentDueDays", e.target.value === "" ? "" : Number(e.target.value))
          }
          placeholder="Kundens standard i PowerOffice"
        />,
      )}
      {field(
        "deliveryEmail",
        "Faktura-e-post",
        <Input
          id="invoice-deliveryEmail"
          type="email"
          className={inputClass("deliveryEmail")}
          value={values.deliveryEmail}
          onChange={(e) => onChange("deliveryEmail", e.target.value)}
          placeholder="Ingen kjent adresse"
          disabled={emailDisabled}
        />,
        <span className="text-[11px] text-muted-foreground/70 ml-1">(mottaker)</span>,
      )}
    </div>
  );
}
